import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  MenuItem, 
  Grid, 
  CircularProgress, 
  Alert, 
  InputLabel, 
  Select, 
  FormControl,
  Card,
  CardContent,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Class as ClassIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { PrimaryButton } from '../ui/StyledComponents';
import supabase from '../../supabaseClient';
import { shiftOptions } from '../../constants/shiftData';

const initialState = {
  name: '',
  description: '',
  teacher_id: '',
  start_date: '',
  shift: '',
};

function CreateClassForm({ onSuccess }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState(initialState);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchTeachers = async () => {
      const { data, error } = await supabase
        .from('teachers')
        .select('id, first_name, last_name')
        .eq('is_active', true);
      if (!error) setTeachers(data || []);
    };
    fetchTeachers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    // Basic validation
    if (!form.name || !form.teacher_id) {
      setError('Class name and teacher are required.');
      setLoading(false);
      return;
    }
    const { error } = await supabase.from('classes').insert({
      name: form.name,
      description: form.description,
      teacher_id: form.teacher_id,
      start_date: form.start_date || null,
      shift: form.shift || null,
    });
    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
      setForm(initialState);
      if (onSuccess) onSuccess();
    }
    setLoading(false);
  };

  return (
    <Card
      sx={{
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
        borderRadius: 3,
        boxShadow: theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 8px 30px rgba(0, 0, 0, 0.4)' 
            : '0 4px 16px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            pb: 2,
            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          }}
        >
          <Box
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(52, 152, 219, 0.2)' 
                : 'rgba(52, 152, 219, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ClassIcon 
              sx={{ 
                fontSize: { xs: 20, sm: 24 }, 
                color: theme.palette.primary.main 
              }} 
            />
          </Box>
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                color: theme.palette.text.primary,
              }}
            >
              Create Class
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                mt: 0.5,
              }}
            >
              Add a new class to the system
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Class Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ClassIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                multiline
                rows={3}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start', pt: 1 }}>
                      <DescriptionIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ position: 'relative' }}>
                <PersonIcon 
                  sx={{ 
                    position: 'absolute',
                    left: 14,
                    top: 18,
                    zIndex: 1,
                    color: theme.palette.text.secondary,
                    fontSize: '1.25rem',
                    pointerEvents: 'none',
                  }} 
                />
                <FormControl 
                  fullWidth 
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      pl: 5,
                    },
                  }}
                >
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Teacher
                  </InputLabel>
                  <Select
                    name="teacher_id"
                    value={form.teacher_id}
                    onChange={handleChange}
                    label="Teacher"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '& .MuiSelect-select': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  >
                    {teachers.map((t) => (
                      <MenuItem key={t.id} value={t.id} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {t.first_name} {t.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                name="start_date"
                type="date"
                value={form.start_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ position: 'relative' }}>
                <AccessTimeIcon 
                  sx={{ 
                    position: 'absolute',
                    left: 14,
                    top: 18,
                    zIndex: 1,
                    color: theme.palette.text.secondary,
                    fontSize: '1.25rem',
                    pointerEvents: 'none',
                  }} 
                />
                <FormControl 
                  fullWidth 
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      pl: 5,
                    },
                  }}
                >
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Shift
                  </InputLabel>
                  <Select
                    name="shift"
                    value={form.shift}
                    onChange={handleChange}
                    label="Shift"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '& .MuiSelect-select': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  >
                    {shiftOptions.map((shift) => (
                      <MenuItem key={shift} value={shift} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {shift}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
            {success && (
              <Grid item xs={12}>
                <Alert severity="success">Class created successfully!</Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Divider 
                sx={{ 
                  my: 2,
                  borderColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.08)',
                }} 
              />
            </Grid>
            <Grid item xs={12}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 2,
                  pt: 1,
                }}
              >
                <PrimaryButton
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '10px 24px', sm: '12px 32px' },
                    borderRadius: 2,
                    fontWeight: 500,
                    textTransform: 'none',
                    boxShadow: !loading
                      ? theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(52, 152, 219, 0.3)'
                        : '0 4px 12px rgba(52, 152, 219, 0.2)'
                      : 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: !loading ? 'translateY(-2px)' : 'none',
                      boxShadow: !loading
                        ? theme.palette.mode === 'dark'
                          ? '0 6px 16px rgba(52, 152, 219, 0.4)'
                          : '0 6px 16px rgba(52, 152, 219, 0.3)'
                        : 'none',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={isMobile ? 20 : 24} color="inherit" />
                  ) : (
                    'Create Class'
                  )}
                </PrimaryButton>
              </Box>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
}

export default CreateClassForm; 